/**
 * UIStateContext - Provides UI state and helper functions
 * 
 * This context provides UI-specific state and utilities that don't
 * fit into other categories but are shared across components.
 */

import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

// Create the context
const UIStateContext = createContext({
    addSwapContext: null,
    hideWarnings: false,
    isMobile: false,
    router: null,
    getDefaultAddSwapSettings: () => ({}),
    getLeftoverOffsetOverlaps: () => [],
});

// Custom hook for consuming UI state
export const useUIState = () => {
    const context = useContext(UIStateContext);
    if (!context) {
        throw new Error('useUIState must be used within UIStateProvider');
    }
    return context;
};

// Provider component
export const UIStateProvider = ({ 
    children,
    addSwapContext,
    hideWarnings = false,
    isMobile = false,
    router = null,
    getDefaultAddSwapSettings,
    getLeftoverOffsetOverlaps,
}) => {
    // Memoize to prevent unnecessary re-renders
    const value = useMemo(() => ({
        addSwapContext,
        hideWarnings,
        isMobile,
        router,
        getDefaultAddSwapSettings: getDefaultAddSwapSettings || (() => ({})),
        getLeftoverOffsetOverlaps: getLeftoverOffsetOverlaps || (() => []),
    }), [addSwapContext, hideWarnings, isMobile, router, getDefaultAddSwapSettings, getLeftoverOffsetOverlaps]);
    
    return (
        <UIStateContext.Provider value={value}>
            {children}
        </UIStateContext.Provider>
    );
};

UIStateProvider.propTypes = {
    children: PropTypes.node.isRequired,
    addSwapContext: PropTypes.object,
    hideWarnings: PropTypes.bool,
    isMobile: PropTypes.bool,
    router: PropTypes.object,
    getDefaultAddSwapSettings: PropTypes.func,
    getLeftoverOffsetOverlaps: PropTypes.func,
};

export default UIStateContext;